import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import API, { fmt } from '@/lib/api';
import { toast } from 'sonner';
import { Plus, Trash, Truck, Receipt, Lightning, FloppyDisk, ArrowClockwise, Camera, Wallet } from '@phosphor-icons/react';
import KlantZoeker from '@/components/KlantZoeker';
import VariantPickerModal from '@/components/VariantPickerModal';
import BarcodeScanner from '@/components/BarcodeScanner';
import { detecteerBtwRegel, klantStandaardBtw } from '@/utils/btwKlant';
import { ScanItemsList } from '@/components/ScanItemsList';
import { ScanProfit } from '@/components/ScanProfit';
import { ScanQuickAdd } from '@/components/ScanQuickAdd';
import { useCompanyFunctions } from '@/contexts/CompanyFunctionsContext';
import { scanPackSize, scanCorrection, scanTotal, scanPiecePrice, scanDocumentLine, scanValid, scanArticle, scanHasContent } from '@/lib/scanQuantity';
import { makeScanItem, scanGroupKey, scanNewestFirst, emptyScanItem, replaceScanArticle } from '@/lib/scanItems';
import { scanDiscountMode, scanDiscountValid, scanDiscountedTotal, switchScanDiscount } from '@/lib/scanDiscount';
import { useScanInput } from '@/hooks/useScanInput';
import { useRepeatScan } from '@/hooks/useRepeatScan';
import { ScanRepeatDialog } from '@/components/ScanRepeatDialog';
import { CashReceiptEditor } from '@/components/cash/CashReceiptEditor';
import { localDate, useCashAccess } from '@/lib/cashReceipts';
import { customerPriceGroup } from '@/lib/customerPricing';
import { applyScanCustomerPrice, scanCustomerNetPrice, scanPriceVariant } from '@/lib/scanCustomerPricing';
import { useScanCustomerPricing } from '@/hooks/useScanCustomerPricing';
import { ScanCustomerPriceDialog } from '@/components/ScanCustomerPriceDialog';

const STORAGE_KEY = 'snel_scan_state_v1';

/**
 * Snel Scanning — Product-scanner voor leverbonnen.
 * Features:
 *  • USB/Bluetooth barcode-scanner ready (auto-focus input)
 *  • Duplicaat-detectie met expliciete keuze voor extra stuks of pakken
 *  • ±-knoppen + automatische verwijder bij 0
 *  • Klant-picker bovenaan → één-klik leverbon aanmaken
 *  • Variant-scanning (EAN per maat/kleur)
 *  • Quick-add bij onbekende EAN (snel nieuw artikel aanmaken zonder pagina wissel)
 *  • Sessie-persistentie via localStorage — werk nooit kwijt bij browser-sluiten
 */
export default function SnelScanLeverbonPage() {
  const navigate = useNavigate();
  const { isVisible } = useCompanyFunctions();
  const cashAccess = useCashAccess();
  const [cashDialog, setCashDialog] = useState(null);
  const saveLock = useRef(false);
  const scanQueue = useRef(Promise.resolve());
  const pendingScans = useRef(0);
  const [items, storeItems] = useState([]);
  const itemsRef = useRef([]);
  // Keep sequential scanner requests and confirmation callbacks on the same latest list.
  const setItems = useCallback(update => {
    const next = typeof update === 'function' ? update(itemsRef.current) : update;
    itemsRef.current = next; storeItems(next);
  }, []);
  const { repeatRequest, askRepeat, confirmRepeat, cancelRepeat } = useRepeatScan(itemsRef, setItems);
  const [inputValue, setInputValue] = useState('');
  const [suggesties, setSuggesties] = useState([]);
  const [suggOpen, setSuggOpen] = useState(false);
  const [suggBezig, setSuggBezig] = useState(false);
  const [laatste, setLaatste] = useState(null);
  const [flash, setFlash] = useState(false);
  const [busy, setBusy] = useState(false);
  const [klanten, setKlanten] = useState([]);
  const [klantId, setKlantId] = useState('');
  const [quickAddOpen, setQuickAddOpen] = useState(null); // { ean, art_code, omschrijving, prijs }
  const [saving, setSaving] = useState(false);
  const [persistLoaded, setPersistLoaded] = useState(false);
  const [variantPicker, setVariantPicker] = useState(null); // { art, itemKey?, prefill? }
  const [cameraOpen, setCameraOpen] = useState(false);
  const [focusRowKey, setFocusRowKey] = useState(null);
  const [rowLookups, setRowLookups] = useState(0);
  const searchingRows = useRef(new Set());
  const [searchBusy, setSearchBusy] = useState(false);
  const reportSearchBusy = useCallback((key, busy) => {
    if (busy) searchingRows.current.add(key); else searchingRows.current.delete(key);
    setSearchBusy(searchingRows.current.size > 0);
  }, []);
  const [invalidTotals, setInvalidTotals] = useState({});
  const reportValidity = useCallback((key, valid) => setInvalidTotals(prev => prev[key] === !valid ? prev : { ...prev, [key]: !valid }), []);
  const inputRef = useRef(null);
  const pricing = useScanCustomerPricing({ klantId, klanten, setKlanten, setKlantId, itemsRef, setItems,
    blocked: !!saving || saveLock.current || busy || !!pendingScans.current || !!rowLookups || searchBusy || !!repeatRequest || !!variantPicker || !!quickAddOpen || !!cashDialog });
  const hasInvalidQuantity = !pricing.ready || items.some(i => !scanHasContent(i) || invalidTotals[i._key] || invalidTotals[`count-${i._key}`] || invalidTotals[`discount-${i._key}`] || invalidTotals[`price-${i._key}`] || invalidTotals[`detail-price-${i._key}`] || invalidTotals[`amount-${i._key}`] || !scanValid(i) || !scanDiscountValid(i, scanTotal(i) * scanPiecePrice(i)));
  const loadCustomers = useCallback(() => API.get('/klanten', { noCache: true, timeout: 30000 }).then(r => setKlanten(Array.isArray(r.data) ? r.data : (r.data?.items || [])))
    .catch(() => toast.error('Klantgegevens konden niet worden geladen.')), []);

  // Laad klanten
  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  // Herstel sessie uit localStorage (één keer bij mount)
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const d = JSON.parse(raw);
        if (Array.isArray(d.items) && d.items.length > 0) {
          setItems(scanNewestFirst(d.items));
          if (d.klantId) setKlantId(d.klantId);
          toast.info(`${d.items.length} gescand${d.items.length > 1 ? 'e' : ''} product${d.items.length > 1 ? 'en' : ''} hersteld uit vorige sessie`, { duration: 4000 });
        }
      }
    } catch { /* silent */ }
    setPersistLoaded(true);
  }, []);

  // Persist bij elke wijziging (alleen nadat we hebben geladen, anders overschrijven we met lege staat)
  useEffect(() => {
    if (!persistLoaded) return;
    try {
      if (items.length > 0) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ items, klantId, ts: Date.now() }));
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch { /* silent */ }
  }, [items, klantId, persistLoaded]);

  useEffect(() => { inputRef.current?.focus(); }, []);

  // Auto-focus naar scan-input zodra klant geselecteerd is (typisch verwacht workflow)
  useEffect(() => {
    if (klantId) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [klantId]);

  const unieke = items.filter(scanHasContent).length;
  const totaal = items.filter(i => scanHasContent(i) && scanTotal(i) > 0).reduce((s, i) => s + (Number(i.aantal) || 0), 0);
  const totaalStuks = items.filter(scanHasContent).reduce((s, i) => s + scanTotal(i), 0);
  const klant = pricing.customer;
  const latestItem = laatste?.art ? items.find(item => item.art_id === laatste.art.id &&
    (item.variant_combo_key || item.variant_code || '') === (laatste.variant?.combo_key || laatste.variant?.art_code || laatste.variant?.code || '')) : null;

  const flashSignal = useCallback(() => {
    setFlash(true);
    setTimeout(() => setFlash(false), 250);
    // Korte success-beep (Web Audio API, geen externe bestanden nodig)
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, ctx.currentTime); // A5
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.12);
      osc.start();
      osc.stop(ctx.currentTime + 0.12);
      setTimeout(() => ctx.close?.(), 300);
    } catch { /* audio context faalt stil (iOS pre-user-interaction) */ }
  }, []);

  const errorSignal = useCallback(() => {
    // Foutgeluid (lagere toon, korter) bij onbekende EAN
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'square';
      osc.frequency.setValueAtTime(220, ctx.currentTime); // A3
      gain.gain.setValueAtTime(0.12, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25);
      osc.start();
      osc.stop(ctx.currentTime + 0.25);
      setTimeout(() => ctx.close?.(), 400);
    } catch { /* silent */ }
  }, []);

  const addOrIncrement = useCallback(async (art, variant, ean, sourceRowKey = null) => {
    if (!pricing.readyRef.current || saveLock.current) return false;
    const groupKey = makeScanItem(art, variant, ean).scan_group_key;
    const existing = itemsRef.current.find(i => scanGroupKey(i) === groupKey && scanTotal(i) > 0);
    if (existing) {
      setCameraOpen(false);
      return askRepeat(existing._key, sourceRowKey === existing._key ? null : sourceRowKey);
    }
    setRowLookups(v => v + 1);
    try {
    const response = await API.get(`/artikels/${encodeURIComponent(art.id)}`, { noCache: true, timeout: 30000 });
    art = scanArticle(response.data);
    if (variant) variant = scanPriceVariant(art, { variant_combo_key: variant.combo_key || variant.art_code || variant.code });
    if (!pricing.readyRef.current || saveLock.current) return false;
    const price = item => applyScanCustomerPrice(item, art, variant, pricing.customerRef.current);
    setItems(prev => {
      const key = variant ? `${art.id}:${variant.combo_key || variant.art_code || variant.code || ''}` : art.id;
      const source = prev.find(i => i._key === sourceRowKey);
      if (source) return [price(replaceScanArticle(source, art, variant)), ...prev.filter(i => i._key !== sourceRowKey)];
      const blank = prev.findIndex(i => !scanHasContent(i));
      if (blank >= 0) return [price(replaceScanArticle(prev[blank], art, variant)), ...prev.filter((_, index) => index !== blank)];
      return [price({
        ...makeScanItem(art, variant, ean),
        _key: prev.some(i => i._key === key) ? `${key}:regel-${crypto.randomUUID()}` : key,
      }), ...prev];
    });
    return true;
    } catch { toast.error('Artikelprijzen konden niet worden geladen. Het artikel is niet toegevoegd.'); return false; }
    finally { setRowLookups(v => Math.max(0, v - 1)); }
  }, [askRepeat, setItems, pricing.readyRef, pricing.customerRef]);

  const zoekEan = useCallback((ean) => {
    if (!pricing.readyRef.current || saveLock.current) return;
    if (!ean || !ean.trim()) return;
    const cleaned = ean.trim();
    pendingScans.current += 1;
    setBusy(true);
    setInputValue(''); setSuggOpen(false); setSuggesties([]);
    const execute = async () => {
    try {
      const r = await API.get(`/artikels/zoek-variant-ean/${encodeURIComponent(cleaned)}`);
      if (r.data?.ok && r.data.art) {
        const art = scanArticle(r.data.art);
        const variant = r.data.variant;
        // Als hoofd-EAN werd geraakt maar artikel heeft actieve varianten → open picker
        if (!variant && art.varianten_actief && Array.isArray(art.varianten) && art.varianten.length > 0) {
          setCameraOpen(false);
          setVariantPicker({ art, prefill: null });
          return;
        }
        const added = await addOrIncrement(art, variant, cleaned);
        if (added) { setLaatste({ ean: cleaned, art, variant }); flashSignal(); }
      } else {
        setCameraOpen(false);
        setLaatste({ ean: cleaned, art: null, fout: 'Niet gevonden' });
        setQuickAddOpen({ ean: cleaned, art_code: '', omschrijving: '', prijs: '' });
        errorSignal();
        toast.info(`EAN ${cleaned} niet gevonden — snel-toevoegen mogelijk`, { duration: 3500 });
      }
    } catch (e) {
      toast.error(`Zoekfout: ${e.response?.data?.detail || e.message}`);
    } finally {
      pendingScans.current -= 1;
      setBusy(pendingScans.current > 0);
      if (!pendingScans.current && document.activeElement === document.body) inputRef.current?.focus();
    }
    };
    scanQueue.current = scanQueue.current.then(execute, execute);
    return scanQueue.current;
  }, [addOrIncrement, flashSignal, errorSignal, pricing.readyRef]);

  const scanInput = useScanInput(setInputValue, zoekEan);

  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === 'Tab' || e.key === 'Escape') scanInput.cancel();
    if (e.key === 'Tab' && /^\d{8,14}$/.test(inputValue.trim())) {
      e.preventDefault(); zoekEan(inputValue); return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      // Enter met exacte 8-14 cijfers → direct EAN-zoek (snelle scan-flow)
      const v = inputValue.trim();
      if (/^\d{8,14}$/.test(v)) {
        setSuggOpen(false);
        zoekEan(v);
        return;
      }
      // Enter met tekst + 1 exact resultaat → voeg die toe
      if (suggesties.length === 1) {
        kiesSuggestie(suggesties[0]);
        return;
      }
      // Anders: start alsnog EAN-zoek (kan toch werken)
      zoekEan(v);
    } else if (e.key === 'Escape') {
      setSuggOpen(false);
    } else if (e.key === 'ArrowDown' && suggesties.length > 0) {
      e.preventDefault();
      setSuggOpen(true);
      // Focus eerste suggestie-item (gebruikt hover-state visueel)
      const el = document.querySelector('[data-testid^="sugg-"]');
      el?.scrollIntoView({ block: 'nearest' });
    }
  };

  const kiesSuggestie = useCallback(async (art) => {
    art = scanArticle(art);
    setInputValue(''); setSuggesties([]); setSuggOpen(false);
    // Backend geeft `_variant_match` mee als deze suggestie een specifieke variant is
    // (bv. zoek "27SW001BEG38" → één match met _variant_match). Anders: als artikel
    // varianten actief heeft maar geen specifieke variant → open picker.
    const vm = art?._variant_match;
    if (vm && vm.combo_key) {
      // Variant-suggestie: direct toevoegen met variant-info
      const variant = { ...vm, art_code: vm.art_code || vm.code };
      const added = await addOrIncrement(art, variant, vm.ean || vm.code || '');
      if (added) { setLaatste({ ean: vm.ean || vm.code || '?', art, variant }); flashSignal(); }
      setTimeout(() => { if (!document.querySelector('[data-testid="scan-repeat-dialog"]')) inputRef.current?.focus(); }, 50);
      return;
    }
    // Hoofdartikel met varianten actief maar geen specifieke variant → open picker
    if (art?.varianten_actief && Array.isArray(art.varianten) && art.varianten.length > 0) {
      setVariantPicker({ art, prefill: null });
      setSuggesties([]);
      setSuggOpen(false);
      return;
    }
    // Gewoon hoofdartikel zonder varianten
    const added = await addOrIncrement(art, null, art.ean || art.art_code || '');
    if (added) { setLaatste({ ean: art.ean || art.art_code || '?', art, variant: null }); flashSignal(); }
    setTimeout(() => { if (!document.querySelector('[data-testid="scan-repeat-dialog"]')) inputRef.current?.focus(); }, 50);
  }, [addOrIncrement, flashSignal]);

  // Debounced autocomplete zodra user typt (min 2 tekens, exclude pure digit EANs ≥8 lang)
  useEffect(() => {
    const v = inputValue.trim();
    if (v.length < 2 || /^\d{8,14}$/.test(v) || quickAddOpen) {
      setSuggesties([]);
      setSuggOpen(false);
      setSuggBezig(false);
      return;
    }
    let cancelled = false;
    const controller = new AbortController();
    setSuggesties([]);
    setSuggOpen(false);
    setSuggBezig(true);
    const handle = setTimeout(async () => {
      try {
        const r = await API.get('/artikels-zoek', { params: { q: v, limit: 8 }, signal: controller.signal });
        if (cancelled) return;
        const arr = Array.isArray(r.data) ? r.data : [];
        setSuggesties(arr);
        setSuggOpen(arr.length > 0);
        // Diagnostic-log — bij problemen op productie kan de user in Browser DevTools
        // (F12 → Console) zien wat de server retourneert. Geen impact op UX.
        if (arr.length === 0) {
          console.info(`[snel-scan] Zoek "${v}" gaf 0 resultaten (endpoint: /artikels-zoek)`);
        }
      } catch (err) {
        if (cancelled) return;
        setSuggesties([]);
        setSuggOpen(false);
        // Zichtbare foutmelding zodat production-issues niet meer stil zijn
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail || err?.message || 'Onbekende fout';
        console.error(`[snel-scan] Zoek-fout "${v}":`, { status, detail, err });
        // Alleen éénmalige toast tonen bij netwerkfout, niet bij elke keystroke
        if (status === 401) {
          if (typeof window !== 'undefined' && !window.__snelscan_auth_warn) {
            window.__snelscan_auth_warn = true;
            toast.error('Sessie verlopen — log opnieuw in');
          }
        } else if (status >= 500) {
          if (typeof window !== 'undefined' && !window.__snelscan_500_warn) {
            window.__snelscan_500_warn = true;
            toast.error(`Server-fout (${status}) bij zoeken. Zie F12 voor details.`);
          }
        } else if (!err?.response) {
          if (typeof window !== 'undefined' && !window.__snelscan_net_warn) {
            window.__snelscan_net_warn = true;
            toast.error('Netwerkfout bij zoeken — controleer internet-verbinding');
          }
        }
      } finally {
        if (!cancelled) setSuggBezig(false);
      }
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(handle);
      controller.abort();
    };
  }, [inputValue, quickAddOpen]);

  const wijzigAantal = (key, delta) => {
    setItems(prev => prev.map(i => i._key === key ? { ...i, aantal: Math.max(0, (i.aantal || 0) + delta) } : i).filter(i => i.aantal > 0));
  };
  // eslint-disable-next-line no-unused-vars
  const _w_a = wijzigAantal; // bewaard voor eventuele toekomstige shortcuts

  // Generieke setter voor grid-bewerkingen (aantal, korting, prijs, prijs_per_stuk)
  const wijzigVeld = (key, veld, waarde) => {
    setItems(prev => prev.map(i => {
      if (i._key !== key) return i;
      let val = waarde;
      if (veld === 'art_code') return { ...i, art_code: waarde, referentie_handmatig: true };
      if (veld === 'korting_mode') return switchScanDiscount(i, waarde, scanTotal(i) * scanPiecePrice(i));
      if (veld === 'eenheidsprijs') {
        const price = Number(String(waarde).replace(',', '.'));
        if (!Number.isFinite(price) || price < 0) return i;
        return { ...i, prijs_handmatig: true, prijs: price * (i.prijs_per_stuk ? 1 : scanPackSize(i)) };
      }
      if (veld === 'totaal_stuks') return { ...i, stuks_correctie: Number(waarde) - Number(i.aantal) * scanPackSize(i) };
      if (veld === 'eenheid_aantal') {
        const size = Number(waarde);
        if (!Number.isInteger(size) || size < 1) return i;
        return { ...i, artikel_pack_grootte: i.artikel_pack_grootte || scanPackSize(i), eenheid_aantal: size,
          stuks_correctie: scanTotal(i) - Number(i.aantal) * size };
      }
      if (veld === 'btw') return { ...i, btw: Math.max(0, Math.min(100, Number(waarde) || 0)), btw_handmatig: true };
      if (veld === 'lijn_totaal') {
        const amount = Number(waarde);
        if (!Number.isFinite(amount) || !scanTotal(i) || amount / scanTotal(i) < 0) return i;
        if (scanDiscountMode(i) === 'euro') {
          const gross = Math.abs(amount) + (Number(i.korting) || 0);
          const piecePrice = gross / Math.abs(scanTotal(i));
          return { ...i, prijs_handmatig: true, prijs: piecePrice * (i.prijs_per_stuk ? 1 : scanPackSize(i)) };
        }
        const discount = Number(i.korting) >= 100 && amount !== 0 ? 0 : Number(i.korting) || 0;
        const piecePrice = discount < 100 ? amount / scanTotal(i) / (1 - discount / 100) : 0;
        return { ...i, prijs_handmatig: true, korting: discount, prijs: piecePrice * (i.prijs_per_stuk ? 1 : scanPackSize(i)) };
      }
      if (veld === 'aantal' || veld === 'korting' || veld === 'prijs') {
        val = parseFloat(String(waarde).replace(',', '.')) || 0;
        if (veld === 'prijs') val = Math.max(0, val);
        if (veld === 'korting') val = Math.max(0, scanDiscountMode(i) === 'euro' ? val : Math.min(100, val));
      }
      // prijs_per_stuk is boolean → laat waarde doorgaan
      return { ...i, ...(['prijs', 'prijs_per_stuk'].includes(veld) ? { prijs_handmatig: true } : {}), ...(veld === 'aantal' ? { stuks_correctie: scanTotal(i) < 0 ? scanTotal(i) - val * scanPackSize(i) : scanCorrection(i) } : {}), [veld]: val };
    }).filter(i => veld === 'aantal' ? i.aantal > 0 : true));
  };
  const verwijderItem = (key) => setItems(prev => prev.filter(i => i._key !== key));

  const addEmptyRow = () => {
    const row = emptyScanItem(); setItems(prev => [row, ...prev]); setFocusRowKey(row._key);
  };
  const applyRowArticle = async (key, art, variant) => {
    const added = await addOrIncrement(art, variant, variant?.ean || art.ean || '', key);
    setFocusRowKey(null);
    return added;
  };
  const selectRowArticle = async (key, candidate) => {
    if (saveLock.current) return;
    setRowLookups(v => v + 1);
    try {
      const art = scanArticle(candidate);
      if (art._variant_match) { await applyRowArticle(key, art, art._variant_match); return; }
      if (art.varianten?.length || art.varianten_actief) {
        const full = scanArticle((await API.get(`/artikels/${art.id}`)).data);
        setVariantPicker({ art: full, itemKey: key, replaceArticle: true }); return;
      }
      await applyRowArticle(key, art, null);
    } catch { toast.error('Artikel kon niet worden geladen. Probeer opnieuw.'); }
    finally { setRowLookups(v => Math.max(0, v - 1)); }
  };

  const openVariant = async (item) => {
    if (saveLock.current || busy || pendingScans.current || rowLookups || !pricing.ready) return;
    setRowLookups(v => v + 1);
    try {
      const loaded = (await API.get(`/artikels/${item.art_id}`, { noCache: true, timeout: 30000 })).data;
      const variants = loaded.varianten?.length ? loaded.varianten : (loaded.variant_matrix?.rijen || []);
      if (!variants.length) { toast.info('Dit artikel heeft geen varianten.'); return; }
      const art = scanArticle(loaded);
      setVariantPicker({ art, itemKey: item._key, prefill: { combo_key: item.variant_combo_key || item.variant_code, combo: item.variant_combo } });
    } catch { toast.error('Kon varianten niet laden.'); }
    finally { setRowLookups(v => Math.max(0, v - 1)); }
  };

  const wisAlles = () => {
    if (items.length === 0) return;
    if (!window.confirm(`Wis alle ${items.length} gescande producten?`)) return;
    setItems([]);
    setLaatste(null);
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* silent */ }
    toast.success('Lijst gewist');
    inputRef.current?.focus();
  };

  // Mapping naar Documenten-form lijn-structuur. Partial-pack (stuks_effectief < eenheid_aantal)
  // wordt geconverteerd naar totale-stuks × per-stuk-prijs zodat de leverbon correct totaliseert.
  const lijnenVoorDoc = () => {
    const regel = detecteerBtwRegel(klant);
    const btwOverride = regel != null ? 0 : klantStandaardBtw(klant);
    return items.map(i => scanDocumentLine(i, btwOverride));
  };

  const openCashReceipt = () => {
    if (saveLock.current || repeatRequest || busy || pendingScans.current || rowLookups || searchingRows.current.size || hasInvalidQuantity || inputValue.trim() || !items.length) return;
    scanInput.cancel();
    setCashDialog({ datum: localDate(), klant_id: klantId, klant_naam: klant?.bedrijf || '', lijnen: lijnenVoorDoc(),
      sourceKey: JSON.stringify(items.map(i => [i._key, i.last_scan_at])) });
  };

  const leverbonOverzetten = () => {
    if (saveLock.current || repeatRequest || busy || pendingScans.current || rowLookups || searchingRows.current.size) return;
    if (hasInvalidQuantity) { toast.error('Controleer de aantallen en kortingen.'); return; }
    if (items.length === 0) { toast.error('Geen producten om over te zetten'); return; }
    const lijnen = lijnenVoorDoc();
    sessionStorage.setItem('snel_scan_lijnen', JSON.stringify(lijnen));
    if (klantId) sessionStorage.setItem('snel_scan_klant_id', klantId);
    else sessionStorage.removeItem('snel_scan_klant_id');
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* silent */ }
    toast.success(`${items.length} product(en) overgezet`);
    navigate(`/documenten/nieuw?type=leverbon&bron=snel_scan${klantId ? '&klant_id=' + klantId : ''}`);
  };

  const documentDirect = async (type) => {
    if (saveLock.current || repeatRequest || busy || pendingScans.current || rowLookups || searchingRows.current.size) return;
    if (hasInvalidQuantity) { toast.error('Controleer de aantallen en kortingen.'); return; }
    if (items.length === 0) { toast.error('Geen producten om te verwerken'); return; }
    if (!klantId) { toast.error('Selecteer eerst een klant voor directe aanmaak'); return; }
    saveLock.current = true;
    setSaving(type);
    try {
      const lijnen = lijnenVoorDoc();
      // Bereken totalen (zoals DocumentFormPage) zodat dashboard/rapporten correct zijn
      const subtotaal = lijnen.reduce((s, l) => s + (l.aantal * l.prijs * (1 - (l.korting || 0) / 100)), 0);
      const btwTotaal = lijnen.reduce((s, l) => s + (l.aantal * l.prijs * (1 - (l.korting || 0) / 100) * ((l.btw || 0) / 100)), 0);
      const totaal = subtotaal + btwTotaal;
      const today = new Date().toISOString().slice(0, 10);
      const r = await API.post('/documenten', {
        type,
        prijsgroep: customerPriceGroup(klant),
        klant_id: klantId,
        status: 'concept',
        ...(type === 'factuur' ? { snel_scan_concept: true } : {}),
        datum: today,
        leverdatum: today,
        taal: klant?.taal || 'nl',
        btw_regel: detecteerBtwRegel(klant) || 'belgie',
        termijn: type === 'factuur' ? 30 : 0,
        einde_maand: false,
        lijnen,
        excl: subtotaal,
        btw_totaal: btwTotaal,
        totaal,
        financiele_korting: 0,
      });
      const docId = r.data?.id;
      if (!docId) throw new Error('Geen documentnummer ontvangen. De scanlijst blijft bewaard.');
      toast.success(`${type === 'factuur' ? 'Conceptfactuur' : 'Leverbon'} ${docId} aangemaakt voor ${klant?.bedrijf || klant?.voorn || 'klant'}`);
      setItems([]);
      setLaatste(null);
      try { localStorage.removeItem(STORAGE_KEY); } catch { /* silent */ }
      navigate(`/documenten/${docId}/bewerk?bron=snel_scan`);
    } catch (e) {
      toast.error(`Aanmaken mislukt: ${e.response?.data?.detail || e.message}`);
    } finally {
      saveLock.current = false;
      setSaving(false);
    }
  };

  // Gefilterde klanten — onnodig nu we KlantZoeker gebruiken, verwijderd

  return (
    <div className="max-w-[1200px] mx-auto min-w-0 pb-8" data-testid="snel-scan-page">
      <fieldset disabled={!!saving || !!repeatRequest || !!cashDialog || !!pricing.request || pricing.busy || !!rowLookups} className="min-w-0 disabled:pointer-events-none" aria-busy={!!saving || !!repeatRequest || !!cashDialog || pricing.busy || !!rowLookups} data-testid="scan-workspace">
      {/* Header */}
      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Lightning size={24} className="text-amber-500" weight="fill" />
          <h1 className="text-xl font-bold text-gray-900">Snel Scanning</h1>
        </div>
        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 border border-amber-300 text-[10px] font-bold uppercase tracking-wider">
          <Truck size={11} weight="fill" /> Leverbon
          <span aria-hidden="true">/</span><Receipt size={11} /> Factuur
        </span>
        {items.length > 0 && persistLoaded && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-bold uppercase tracking-wider" title="Lijst wordt automatisch bewaard — veilig browser sluiten">
            <FloppyDisk size={11} weight="fill" /> Auto-opgeslagen
          </span>
        )}
      </div>

      {/* Klant-picker bovenaan — zelfde stijl als documenten-flow */}
      <div className="bg-white border border-[#DBEAFE] p-3 mb-4" data-testid="klant-picker-card">
        <label className="text-xs text-gray-500 flex items-center gap-1.5 mb-2">
          Klant voor dit document
        </label>
        <KlantZoeker klanten={klanten} klantId={klantId} onChange={pricing.choose} />
        <div className="mt-2 flex flex-wrap items-center gap-3 text-xs">
          <span data-testid="scan-customer-price-group">Verkoopgroep: <strong className="capitalize">{klantId && !klant ? '—' : customerPriceGroup(klant)}</strong></span>
          {items.some(i => i.art_id) && <button type="button" className="text-[#002FA7] underline" onClick={() => pricing.choose(klantId, true)} data-testid="scan-reapply-customer-prices">Klantprijzen opnieuw toepassen</button>}
        </div>
        {klantId && !klant && <div role="alert" className="mt-2 text-sm text-red-700" data-testid="scan-customer-load-error">Klantgegevens zijn niet beschikbaar. <button type="button" className="underline" onClick={loadCustomers} data-testid="scan-retry-customer-load">Opnieuw laden</button></div>}
        {klant && (() => {
          const regel = detecteerBtwRegel(klant);
          const std = klantStandaardBtw(klant);
          if (!regel && std == null) return null;
          const txt = regel === 'intracommunautair'
            ? `Intracommunautaire levering (${klant.land || 'EU'}) — 0% BTW wordt toegepast`
            : regel === 'export_eu'
              ? `Export buiten EU (${klant.land || ''}) — 0% BTW wordt toegepast`
              : `Klant-BTW ${std}% wordt toegepast op alle lijnen`;
          return (
            <div className="mt-2 px-2 py-1.5 bg-amber-50 border border-amber-200 text-[11px] text-amber-900" data-testid="snelscan-btw-banner">
              🌍 {txt}
            </div>
          );
        })()}
        {klant && (
          <div className="mt-2 text-xs text-cyan-800 bg-cyan-50 px-2 py-1 border border-cyan-200 inline-block" data-testid="klant-geselecteerd">
            ✓ <strong>{klant.bedrijf || `${klant.voorn || ''} ${klant.achtern || ''}`.trim()}</strong>
            {klant.gemeente && <> · {klant.gemeente}</>}
            {klant.taal && klant.taal !== 'nl' && <> · taal: {klant.taal.toUpperCase()}</>}
          </div>
        )}
      </div>

      {/* Scanner-input */}
      <div
        className={`bg-white border rounded-md p-3 mb-5 transition-colors ${flash ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}
        data-testid="scan-input-card"
      >
        <div className="flex items-end gap-2 flex-wrap">
          <div className="w-full sm:flex-1 sm:w-auto min-w-0 relative">
            <label className="text-xs text-gray-500 mb-1 flex items-center gap-1.5" htmlFor="snel-scan-barcode">
              <span>Barcode of artikel</span>
              {suggBezig && <span data-testid="scan-autocomplete-loading" role="status" className="text-[9px] text-gray-500 italic animate-pulse">zoeken…</span>}
            </label>
            <input
              ref={inputRef}
              id="snel-scan-barcode"
              type="text"
              className="field-input text-sm !py-2.5 bg-white focus:border-blue-600"
              value={inputValue}
              onChange={e => scanInput.change(e.target.value)}
              onKeyDown={onKeyDown}
              onFocus={() => { if (suggesties.length > 0) setSuggOpen(true); }}
              placeholder="Scan EAN, typ artikelcode of omschrijving…"
              autoFocus
              autoComplete="off"
              data-testid="scan-input"
              disabled={quickAddOpen !== null || !pricing.ready}
            />
            {/* Autocomplete dropdown */}
            {suggOpen && suggesties.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border-2 border-blue-400 shadow-xl z-20 max-h-[360px] overflow-y-auto" data-testid="sugg-dropdown">
                <div className="px-3 py-1.5 bg-blue-50 text-[10px] font-bold text-[#002FA7] uppercase tracking-wider border-b border-blue-200 sticky top-0">
                  {suggesties.length} resultat{suggesties.length > 1 ? 'en' : ''} — klik om toe te voegen
                </div>
                {suggesties.map((s, idx) => (
                  <div
                    key={s.id}
                    className="px-3 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 text-sm flex items-center gap-3"
                    onClick={() => kiesSuggestie(s)}
                    data-testid={`sugg-${s.id}${s._variant_match ? `-${s._variant_match.combo_key || s._variant_match.art_code || s._variant_match.code || idx}` : ''}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-gray-900 truncate">{s.omschrijving}</div>
                      <div className="flex gap-2 items-center text-[11px] font-mono text-gray-500 mt-0.5 flex-wrap">
                        {s.art_code && <span className="bg-gray-100 px-1.5 py-0.5">{s.art_code}</span>}
                        {s.ean && <span className="text-[#002FA7] font-bold">EAN {s.ean}</span>}
                        {s.eenheid && (
                          <span className="bg-amber-50 text-amber-800 border border-amber-200 px-1.5 py-0.5">
                            {s.eenheid}
                            {s.eenheid_aantal > 0 && ` × ${s.eenheid_aantal}${s.eenheid_label ? ' ' + s.eenheid_label : ''}`}
                          </span>
                        )}
                        {s.categorie && <span className="text-purple-600">· {s.categorie}</span>}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className={`text-xs font-mono font-bold ${(s.voorraad ?? 0) < 5 ? 'text-amber-600' : 'text-emerald-700'}`}>
                        V: {s.voorraad ?? '-'}
                      </div>
                      <div className="text-[10px] text-gray-600" data-testid={`scan-suggestion-price-${s.id}-${s._variant_match?.combo_key || 'master'}-${idx}`}>{fmt(scanCustomerNetPrice(s, s._variant_match, klant))} netto / eenheid</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <button type="button" className="btn btn-secondary btn-lg inline-flex items-center gap-2 whitespace-nowrap"
                  onClick={() => setCameraOpen(true)}
                  disabled={busy || quickAddOpen !== null || !pricing.ready}
                  title="Scan met camera (telefoon/tablet)"
                  data-testid="camera-scan-btn">
            <Camera size={18} weight="bold" /> Camera
          </button>
          <button type="button" className="btn btn-primary btn-lg inline-flex items-center gap-2 whitespace-nowrap"
                  onClick={() => zoekEan(inputValue)}
                  disabled={busy || !inputValue.trim() || quickAddOpen !== null || !pricing.ready}
                  data-testid="scan-btn">
            <Plus size={16} weight="bold" /> Scannen
          </button>
          {cashAccess.allowed && isVisible('/dagontvangsten') && <Link className="btn btn-secondary inline-flex items-center justify-center gap-2 shrink-0" to="/dagontvangsten" data-testid="cash-overview-link"><Wallet size={17} />Dagontvangsten</Link>}
        </div>
        {laatste && !quickAddOpen && (
          <div className={`mt-3 p-2 text-xs ${laatste.fout ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-emerald-50 text-emerald-800 border border-emerald-200'}`}
               data-testid="laatste-scan">
            {laatste.fout ? (
              <>❌ <span className="font-mono font-bold">{laatste.ean}</span> — {laatste.fout}</>
            ) : (
              <>
                ✅ Laatst gescand: <span className="font-mono font-bold">{laatste.ean}</span> →
                <strong className="ml-1">{laatste.art?.omschrijving}</strong>
                {laatste.variant?.combo && (
                  <span className="ml-2 px-1.5 py-0.5 bg-purple-100 text-purple-800 text-[10px] font-mono font-bold rounded">
                    {Object.entries(laatste.variant.combo).map(([k, v]) => `${k}: ${v}`).join(' · ')}
                  </span>
                )}
                {latestItem && <span className="ml-2 text-gray-500" data-testid="scan-latest-line-total">· Regel: {fmt(scanDiscountedTotal(latestItem, scanTotal(latestItem) * scanPiecePrice(latestItem)))}</span>}
              </>
            )}
          </div>
        )}
      </div>

      {/* Quick-add dialog — inline onder scanner */}
      {quickAddOpen && <ScanQuickAdd key={quickAddOpen.ean} ean={quickAddOpen.ean} onBusyChange={setSaving}
        onClose={() => { setQuickAddOpen(null); setTimeout(() => inputRef.current?.focus(), 100); }}
        onCreated={async newArt => {
          const added = await addOrIncrement(newArt, null, newArt.ean);
          setQuickAddOpen(null);
          if (added) {
            setLaatste({ ean: newArt.ean, art: newArt, variant: null }); flashSignal();
            toast.success(`Artikel "${newArt.omschrijving}" opgeslagen en toegevoegd`);
          } else toast.warning('Artikel opgeslagen, maar niet aan de scanlijst toegevoegd. Scan het opnieuw.');
          setTimeout(() => inputRef.current?.focus(), 100);
        }} />}

      <div className="flex items-center justify-between gap-3 mb-3">
        <h2 className="text-base font-semibold text-gray-800" data-testid="scan-list-title">Gescande producten</h2>
        <button type="button" className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-red-600 disabled:opacity-40" onClick={wisAlles} disabled={!items.length} data-testid="wis-alles-btn"><Trash size={14} /> Alles wissen</button>
      </div>
      <ScanItemsList items={items} onChange={wijzigVeld} onRemove={verwijderItem} onVariant={openVariant} onValidityChange={reportValidity} onSearchBusy={reportSearchBusy}
        onAddRow={addEmptyRow} onSelectArticle={selectRowArticle} focusRowKey={focusRowKey} />
      <footer className="flex flex-wrap items-end justify-between gap-5 mt-5 pt-5 border-t border-gray-200" data-testid="scan-summary">
        <div className="min-w-0 max-w-full">
          <div className="text-sm font-bold text-black uppercase"><span data-testid="scan-grand-total-label">Totaal excl. btw</span> <strong className="ml-2 text-xl text-black" data-testid="scan-grand-total">{hasInvalidQuantity ? '—' : fmt(items.reduce((sum, i) => sum + scanDiscountedTotal(i, scanTotal(i) * scanPiecePrice(i)), 0))}</strong></div>
          <ScanProfit items={items} invalid={hasInvalidQuantity} saving={!!saving} />
          <div className="grid grid-cols-3 gap-2 text-xs font-bold text-black uppercase mt-3" data-testid="scan-counts-grid">
            <span className="min-w-0 border border-gray-400 bg-white px-2 py-2 text-center break-words" data-testid="stat-uniek">{unieke} {unieke === 1 ? 'product' : 'producten'}</span>
            <span className="min-w-0 border border-gray-400 bg-white px-2 py-2 text-center break-words" data-testid="stat-packs">Aantal: {totaal}</span>
            <span className="min-w-0 border border-gray-400 bg-white px-2 py-2 text-center break-words" data-testid="stat-totaal">{totaalStuks} {totaalStuks === 1 ? 'stuk' : 'stuks'}</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          <button type="button" className="btn btn-secondary gap-2 flex-1 sm:flex-none" onClick={leverbonOverzetten} disabled={!items.length || !!saving || busy || !!rowLookups || searchBusy || hasInvalidQuantity} data-testid="leverbon-form-btn"><ArrowClockwise size={16} /> Via formulier</button>
          <button type="button" className="btn btn-primary gap-2 flex-1 sm:flex-none" onClick={() => documentDirect('leverbon')} title={!klantId ? 'Selecteer eerst een klant' : 'Direct leverbon aanmaken'} disabled={!items.length || !klantId || !!saving || busy || !!rowLookups || searchBusy || hasInvalidQuantity} data-testid="leverbon-direct-btn"><Truck size={16} /> {saving === 'leverbon' ? 'Opslaan…' : 'Direct leverbon'}</button>
          {isVisible('/facturen') && <button type="button" className="btn btn-primary gap-2 flex-1 sm:flex-none" onClick={() => documentDirect('factuur')} title={!klantId ? 'Selecteer eerst een klant' : 'Factuur als concept openen'} disabled={!items.length || !klantId || !!saving || busy || !!rowLookups || searchBusy || hasInvalidQuantity} data-testid="factuur-direct-btn"><Receipt size={16} /> {saving === 'factuur' ? 'Opslaan…' : 'Direct factuur'}</button>}
          {cashAccess.allowed && cashAccess.writable && isVisible('/dagontvangsten') && <button type="button" className="btn bg-emerald-600 text-white hover:bg-emerald-700 gap-2 flex-1 sm:flex-none" onClick={openCashReceipt} disabled={!items.length || !!saving || busy || !!rowLookups || searchBusy || hasInvalidQuantity || !!inputValue.trim()} data-testid="cash-direct-button"><Wallet size={16} />Dagontvangsten</button>}
        </div>
      </footer>

      {/* Variant-picker modal — opent bij artikel met varianten, of 🎨 klik op bestaande rij */}
      {variantPicker && (
        <VariantPickerModal
          artikel={variantPicker.art}
          prijsGroep={customerPriceGroup(klant)}
          customerPrice={variant => scanCustomerNetPrice(variantPicker.art, variant, klant)}
          open={true}
          preselectVariant={variantPicker.prefill || null}
          onClose={() => setVariantPicker(null)}
          onSelect={async (variant) => {
            let art = variantPicker.art;
            if (variantPicker.itemKey && !variantPicker.replaceArticle) {
              setRowLookups(v => v + 1);
              try {
                art = scanArticle((await API.get(`/artikels/${encodeURIComponent(art.id)}`, { noCache: true, timeout: 30000 })).data);
                variant = scanPriceVariant(art, { variant_combo_key: variant.combo_key || variant.art_code || variant.code });
              } catch {
                toast.error('Deze variant is niet meer beschikbaar of kon niet worden geladen. De regel is ongewijzigd.');
                return;
              } finally { setRowLookups(v => Math.max(0, v - 1)); }
            }
            setVariantPicker(null);
            if (variantPicker.replaceArticle) {
              await applyRowArticle(variantPicker.itemKey, art, variant); return;
            }
            if (variantPicker.itemKey) {
              const newKey = `${art.id}:${variant.combo_key || variant.art_code || variant.code || ''}`;
              const target = itemsRef.current.find(i => i._key === variantPicker.itemKey);
              if (target && scanTotal(target) > 0 && itemsRef.current.some(i => i._key !== variantPicker.itemKey && scanTotal(i) > 0 && scanGroupKey(i) === newKey)) {
                await addOrIncrement(art, variant, variant.ean || '', variantPicker.itemKey); return;
              }
              // Wijzig bestaande item — vervang variant-info
              setItems(prev => prev.map(i => {
                if (i._key !== variantPicker.itemKey) return i;
                const size = Number(variant.pack_grootte || variant.eenheid_aantal || art.pack_grootte || art.eenheid_aantal) || 1;
                const keepCustomSize = i.artikel_pack_grootte && scanPackSize(i) !== i.artikel_pack_grootte;
                return applyScanCustomerPrice({
                  ...i,
                  scan_group_key: newKey,
                  variant_code: variant.art_code || variant.code || variant.combo_key || '',
                  variant_combo_key: variant.combo_key || variant.art_code || variant.code || '',
                  variant_combo: variant.combo || null,
                  variant_label: variant.combo ? Object.entries(variant.combo).map(([k, v]) => `${k}: ${v}`).join(' · ') : '',
                  ean: variant.ean || i.ean,
                  voorraad: variant.voorraad ?? i.voorraad,
                  prijs: makeScanItem(art, variant).prijs,
                  prijs_per_stuk: makeScanItem(art, variant).prijs_per_stuk,
                  prijs_handmatig: false,
                  artikel_pack_grootte: size,
                  eenheid_aantal: keepCustomSize ? i.eenheid_aantal : size,
                  stuks_correctie: scanCorrection(i),
                }, art, variant, pricing.customerRef.current);
              }));
            } else {
              const added = await addOrIncrement(art, variant, variant.ean || variant.art_code || '');
              if (added) { setLaatste({ ean: variant.ean || variant.art_code || '?', art, variant }); flashSignal(); }
            }
            setVariantPicker(null);
            setTimeout(() => { if (!document.querySelector('[data-testid="scan-repeat-dialog"]')) inputRef.current?.focus(); }, 100);
          }}
        />
      )}

      {/* Camera barcode-scanner — modal voor telefoon/tablet */}
      <BarcodeScanner
        open={cameraOpen}
        onClose={() => {
          setCameraOpen(false);
          setTimeout(() => inputRef.current?.focus(), 100);
        }}
        title="Scan EAN/barcode"
        continuous={true}
        onDetect={(code) => {
          // Continue-scan: voeg toe en hou modal open zodat user de volgende kan scannen
          setInputValue('');
          zoekEan(code);
          return true; // keep open
        }}
      />
      </fieldset>
      {pricing.request && <ScanCustomerPriceDialog request={pricing.request} busy={pricing.busy} error={pricing.error} onConfirm={pricing.confirm} onCancel={pricing.cancel} />}
      {cashDialog && <CashReceiptEditor initial={cashDialog} onClose={() => setCashDialog(null)} onSaved={receipt => {
        setCashDialog(null); setItems([]); setLaatste(null);
        try { localStorage.removeItem(STORAGE_KEY); } catch { /* storage may be unavailable */ }
        toast.success(`Cashontvangst ${receipt.nummer} opgeslagen.`);
        navigate(`/dagontvangsten?maand=${receipt.datum.slice(0, 7)}`);
      }} />}
      {repeatRequest && items.find(i => i._key === repeatRequest.itemKey) && <ScanRepeatDialog key={repeatRequest.id}
        item={items.find(i => i._key === repeatRequest.itemKey)} initialQuantity={repeatRequest.initialQuantity}
        replacingRow={!!repeatRequest.sourceKey} onConfirm={confirmRepeat} onCancel={cancelRepeat}
        onReturnFocus={() => setTimeout(() => { if (!document.querySelector('[data-testid="scan-repeat-dialog"]')) inputRef.current?.focus(); }, 50)} />}
    </div>
  );
}
